import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatPrice, formatImageUrl, orderStatusNames, categoryNames } from '../utils/helpers';
import { BarChart3, Package, ShoppingBag, Users, Plus, Edit, Trash2, Truck, Sparkles, FileText, Camera, CreditCard, Eye } from 'lucide-react';
import ProductModal from '../components/ProductModal';
import ServiceModal from '../components/ServiceModal';
import ServiceCategoryModal from '../components/ServiceCategoryModal';
import PostModal from '../components/PostModal';
import GalleryModal from '../components/GalleryModal';
import PaymentMethodModal from '../components/PaymentMethodModal';
import OrderDetailModal from '../components/OrderDetailModal';
import OrdersManager from '../components/OrdersManager';
import { useToast } from '../context/ToastContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShippingModal = ({ isOpen, onClose, shipping, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    estimated_days: '',
    active: true
  });

  useEffect(() => {
    if (shipping) {
      setFormData({
        name: shipping.name || '',
        price: shipping.price || '',
        estimated_days: shipping.estimated_days || '',
        active: shipping.active !== undefined ? shipping.active : true
      });
    } else {
      setFormData({
        name: '',
        price: '',
        estimated_days: '',
        active: true
      });
    }
  }, [shipping, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="glass rounded-2xl max-w-md w-full">
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-2xl font-semibold">
            {shipping ? 'Futárszolgálat szerkesztése' : 'Új futárszolgálat'}
          </h2>
          <button onClick={onClose} className="text-2xl hover:text-yellow-200">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Név *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              placeholder="GLS Futárszolgálat"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Ár (HUF) *</label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              required
              min="0"
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              placeholder="1990"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Szállítási idő *</label>
            <input
              type="text"
              value={formData.estimated_days}
              onChange={(e) => setFormData({ ...formData, estimated_days: e.target.value })}
              required
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              placeholder="1-2 munkanap"
            />
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-gray-300">Aktív (látható a pénztárban)</span>
            </label>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 glass rounded-lg py-3 hover:bg-white/5 transition"
            >
              Mégse
            </button>
            <button
              type="submit"
              className="flex-1 btn-magic rounded-lg py-3 font-semibold"
            >
              {shipping ? 'Módosítás' : 'Létrehozás'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Admin = () => {
  const { user, loading: authLoading } = useAuth();  // Rename to avoid conflict
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [aboutContent, setAboutContent] = useState('');
  const [galleryItems, setGalleryItems] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [orders, setOrders] = useState([]);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [editingGalleryItem, setEditingGalleryItem] = useState(null);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingShipping, setEditingShipping] = useState(null);
  const [showShippingModal, setShowShippingModal] = useState(false);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, authLoading, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const response = await axios.get(`${API_URL}/admin/dashboard`);
        setStats(response.data);
      } else if (activeTab === 'products') {
        const response = await axios.get(`${API_URL}/products?limit=1000`);
        setProducts(response.data);
      } else if (activeTab === 'services') {
        const [servicesRes, categoriesRes] = await Promise.all([
          axios.get(`${API_URL}/services?active_only=false`),
          axios.get(`${API_URL}/service-categories`)
        ]);
        setServices(servicesRes.data);
        setServiceCategories(categoriesRes.data);
      } else if (activeTab === 'blog') {
        const response = await axios.get(`${API_URL}/posts?active_only=false`);
        setPosts(response.data);
      } else if (activeTab === 'about') {
        const response = await axios.get(`${API_URL}/about`);
        setAboutContent(response.data.content);
      } else if (activeTab === 'gallery') {
        const response = await axios.get(`${API_URL}/gallery`);
        setGalleryItems(response.data);
      } else if (activeTab === 'payment-methods') {
        console.log('Fetching payment methods...');
        const response = await axios.get(`${API_URL}/payment-methods?active_only=false`);
        console.log('Payment methods received:', response.data.length, response.data);
        setPaymentMethods(response.data);
      } else if (activeTab === 'orders') {
        const response = await axios.get(`${API_URL}/orders?limit=1000`);
        setOrders(response.data);
      } else if (activeTab === 'shipping') {
        const response = await axios.get(`${API_URL}/shipping-options/all`);
        setShippingOptions(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt a terméket?')) return;
    
    try {
      await axios.delete(`${API_URL}/products/${productId}`);
      fetchData();
    } catch (error) {
      alert('Hiba történt a törlés során');
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API_URL}/orders/${orderId}`, { status: newStatus });
      fetchData();
    } catch (error) {
      addToast('Hiba történt a státusz frissítése során', 'error');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await axios.delete(`${API_URL}/orders/${orderId}`);
      fetchData();
    } catch (error) {
      addToast('Hiba történt a törlés során', 'error');
    }
  };

  const handleOpenOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  };

  const handleDeleteShipping = async (shippingId) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt a futárszolgálatot?')) return;
    
    try {
      await axios.delete(`${API_URL}/shipping-options/${shippingId}`);
      addToast('Futárszolgálat törölve!', 'success');
      fetchData();
    } catch (error) {
      addToast('Hiba történt a törlés során', 'error');
    }
  };

  const handleSaveShipping = async (shippingData) => {
    try {
      if (editingShipping) {
        await axios.put(`${API_URL}/shipping-options/${editingShipping.id}`, shippingData);
        addToast('Futárszolgálat módosítva!', 'success');
      } else {
        await axios.post(`${API_URL}/shipping-options`, shippingData);
        addToast('Futárszolgálat létrehozva!', 'success');
      }
      setShowShippingModal(false);
      setEditingShipping(null);
      fetchData();
    } catch (error) {
      addToast('Hiba történt a mentés során', 'error');
    }
  };

  // Service Category Handlers
  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt a kategóriát?')) return;
    
    try {
      await axios.delete(`${API_URL}/service-categories/${categoryId}`);
      addToast('Kategória törölve!', 'success');
      fetchData();
    } catch (error) {
      addToast(error.response?.data?.detail || 'Hiba történt a törlés során', 'error');
    }
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/service-categories/${editingCategory.id}`, categoryData);
        addToast('Kategória módosítva!', 'success');
      } else {
        await axios.post(`${API_URL}/service-categories`, categoryData);
        addToast('Kategória létrehozva!', 'success');
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      fetchData();
    } catch (error) {
      addToast('Hiba történt a mentés során', 'error');
    }
  };

  // Service Handlers
  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt a szolgáltatást?')) return;
    
    try {
      await axios.delete(`${API_URL}/services/${serviceId}`);
      addToast('Szolgáltatás törölve!', 'success');
      fetchData();
    } catch (error) {
      addToast('Hiba történt a törlés során', 'error');
    }
  };

  const handleSaveService = async (serviceData) => {
    try {
      if (editingService) {
        await axios.put(`${API_URL}/services/${editingService.id}`, serviceData);
        addToast('Szolgáltatás módosítva!', 'success');
      } else {
        await axios.post(`${API_URL}/services`, serviceData);
        addToast('Szolgáltatás létrehozva!', 'success');
      }
      setShowServiceModal(false);
      setEditingService(null);
      fetchData();
    } catch (error) {
      addToast('Hiba történt a mentés során', 'error');
    }
  };

  // Post Handlers
  const handleDeletePost = async (postId) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt a bejegyzést?')) return;
    
    try {
      await axios.delete(`${API_URL}/posts/${postId}`);
      addToast('Bejegyzés törölve!', 'success');
      fetchData();
    } catch (error) {
      addToast('Hiba történt a törlés során', 'error');
    }
  };

  const handleSavePost = async (postData) => {
    try {
      if (editingPost) {
        await axios.put(`${API_URL}/posts/${editingPost.id}`, postData);
        addToast('Bejegyzés módosítva!', 'success');
      } else {
        await axios.post(`${API_URL}/posts`, postData);
        addToast('Bejegyzés létrehozva!', 'success');
      }
      setShowPostModal(false);
      setEditingPost(null);
      fetchData();
    } catch (error) {
      addToast('Hiba történt a mentés során', 'error');
    }
  };

  // About Content Handler
  const handleSaveAbout = async () => {
    try {
      await axios.put(`${API_URL}/about`, { content: aboutContent });
      addToast('Rólam szekció frissítve!', 'success');
    } catch (error) {
      addToast('Hiba történt a mentés során', 'error');
    }
  };


  // Gallery Handlers
  const handleDeleteGalleryItem = async (itemId) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt a galéria elemet?')) return;
    
    try {
      await axios.delete(`${API_URL}/gallery/${itemId}`);
      addToast('Galéria elem törölve!', 'success');
      fetchData();
    } catch (error) {
      addToast('Hiba történt a törlés során', 'error');
    }
  };

  const handleSaveGalleryItem = async (galleryData) => {
    try {
      if (editingGalleryItem) {
        await axios.put(`${API_URL}/gallery/${editingGalleryItem.id}`, galleryData);
        addToast('Galéria elem módosítva!', 'success');
      } else {
        await axios.post(`${API_URL}/gallery`, galleryData);
        addToast('Galéria elem létrehozva!', 'success');
      }
      setShowGalleryModal(false);
      setEditingGalleryItem(null);
      fetchData();
    } catch (error) {
      addToast('Hiba történt a mentés során', 'error');
    }
  };

  // Payment Methods Handlers
  const handleDeletePaymentMethod = async (methodId) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt a fizetési módot?')) return;
    
    try {
      await axios.delete(`${API_URL}/payment-methods/${methodId}`);
      addToast('Fizetési mód törölve!', 'success');
      fetchData();
    } catch (error) {
      addToast('Hiba történt a törlés során', 'error');
    }
  };

  const handleSavePaymentMethod = async (paymentData) => {
    try {
      if (editingPaymentMethod) {
        await axios.put(`${API_URL}/payment-methods/${editingPaymentMethod.id}`, paymentData);
        addToast('Fizetési mód módosítva!', 'success');
      } else {
        await axios.post(`${API_URL}/payment-methods`, paymentData);
        addToast('Fizetési mód létrehozva!', 'success');
      }
      setShowPaymentModal(false);
      setEditingPaymentMethod(null);
      fetchData();
    } catch (error) {
      addToast('Hiba történt a mentés során', 'error');
    }
  };

  const handleTogglePaymentMethod = async (methodId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/payment-methods/${methodId}`, { active: !currentStatus });
      addToast('Fizetési mód állapota frissítve!', 'success');
      fetchData();
    } catch (error) {
      addToast('Hiba történt a frissítés során', 'error');
    }
  };

  // Check admin access AFTER all handler functions are defined
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-200 mb-4"></div>
          <p className="text-gray-400">Betöltés...</p>
        </div>
      </div>
    );
  }
  
  if (!user || user.role !== 'admin') return null;

  return (
    <div className="relative min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="section-title text-4xl md:text-5xl mb-8 text-center">
          <span>Admin Dashboard</span>
        </h1>

        {/* Tabs */}
        <div className="glass rounded-xl p-2 mb-8 flex flex-wrap gap-2">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'dashboard'
                ? 'bg-yellow-200 text-black font-semibold'
                : 'hover:bg-white/5'
            }`}
          >
            <BarChart3 className="w-5 h-5 inline mr-2" />
            Áttekintés
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'products'
                ? 'bg-yellow-200 text-black font-semibold'
                : 'hover:bg-white/5'
            }`}
          >
            <Package className="w-5 h-5 inline mr-2" />
            Termékek
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'orders'
                ? 'bg-yellow-200 text-black font-semibold'
                : 'hover:bg-white/5'
            }`}
          >
            <ShoppingBag className="w-5 h-5 inline mr-2" />
            Rendelések
          </button>
          <button
            onClick={() => setActiveTab('shipping')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'shipping'
                ? 'bg-yellow-200 text-black font-semibold'
                : 'hover:bg-white/5'
            }`}
          >
            <Truck className="w-5 h-5 inline mr-2" />
            Szállítás
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'services'
                ? 'bg-yellow-200 text-black font-semibold'
                : 'hover:bg-white/5'
            }`}
          >
            <Sparkles className="w-5 h-5 inline mr-2" />
            Szolgáltatások
          </button>
          <button
            onClick={() => setActiveTab('blog')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'blog'
                ? 'bg-yellow-200 text-black font-semibold'
                : 'hover:bg-white/5'
            }`}
          >
            <FileText className="w-5 h-5 inline mr-2" />
            Blog & Podcast
          </button>
          <button
            onClick={() => setActiveTab('about')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'about'
                ? 'bg-yellow-200 text-black font-semibold'
                : 'hover:bg-white/5'
            }`}
          >
            <Users className="w-5 h-5 inline mr-2" />
            Rólam
          </button>
          <button
            onClick={() => setActiveTab('gallery')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'gallery'
                ? 'bg-yellow-200 text-black font-semibold'
                : 'hover:bg-white/5'
            }`}
          >
            <Camera className="w-5 h-5 inline mr-2" />
            Galéria
          </button>
          <button
            onClick={() => setActiveTab('payment-methods')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'payment-methods'
                ? 'bg-yellow-200 text-black font-semibold'
                : 'hover:bg-white/5'
            }`}
          >
            <CreditCard className="w-5 h-5 inline mr-2" />
            Fizetési módok
          </button>

        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-400">Betöltés...</p>
          </div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && stats && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="glass rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <ShoppingBag className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{stats.total_orders}</p>
                        <p className="text-sm text-gray-400">Rendelések</p>
                      </div>
                    </div>
                  </div>
                  <div className="glass rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <Package className="w-6 h-6 text-green-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{stats.total_products}</p>
                        <p className="text-sm text-gray-400">Termékek</p>
                      </div>
                    </div>
                  </div>
                  <div className="glass rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-pink-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{stats.total_services || 0}</p>
                        <p className="text-sm text-gray-400">Szolgáltatások</p>
                      </div>
                    </div>
                  </div>
                  <div className="glass rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{stats.total_posts || 0}</p>
                        <p className="text-sm text-gray-400">Blog & Podcast</p>
                      </div>
                    </div>
                  </div>
                  <div className="glass rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <Users className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{stats.total_users}</p>
                        <p className="text-sm text-gray-400">Felhasználók</p>
                      </div>
                    </div>
                  </div>
                  <div className="glass rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-6 h-6 text-yellow-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{formatPrice(stats.total_revenue)}</p>
                        <p className="text-sm text-gray-400">Bevétel</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Orders */}
                <div className="glass rounded-xl p-6">
                  <h2 className="text-xl font-semibold mb-4">Legutóbbi rendelések</h2>
                  <div className="space-y-3">
                    {stats.recent_orders.slice(0, 5).map((order) => (
                      <div key={order.id} className="flex justify-between items-center bg-black/20 rounded-lg p-3">
                        <div>
                          <p className="font-semibold">#{order.order_number}</p>
                          <p className="text-sm text-gray-400">{new Date(order.created_at).toLocaleDateString('hu-HU')}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-yellow-200">{formatPrice(order.total)}</p>
                          <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400">
                            {orderStatusNames[order.status]}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Products Tab */}
            {activeTab === 'products' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-semibold">Termékek kezelése</h2>
                  <button
                    onClick={() => {
                      setEditingProduct(null);
                      setShowProductModal(true);
                    }}
                    className="btn-magic rounded-lg px-4 py-2 flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Új termék
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map((product) => (
                    <div key={product.id} className="glass rounded-xl overflow-hidden">
                      <div className="aspect-square bg-black/30">
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={formatImageUrl(product.images[0])}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-6xl">✨</span>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="text-xs text-yellow-200/70 mb-1">
                          {categoryNames[product.category]}
                        </div>
                        <h3 className="font-semibold mb-2">{product.name}</h3>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-yellow-200 font-bold">{formatPrice(product.price)}</span>
                          <span className="text-sm text-gray-400">Raktár: {product.stock}</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingProduct(product);
                              setShowProductModal(true);
                            }}
                            className="flex-1 bg-white/10 hover:bg-white/20 rounded-lg py-2 transition"
                          >
                            <Edit className="w-4 h-4 inline" />
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product.id)}
                            className="flex-1 bg-red-500/20 hover:bg-red-500/30 rounded-lg py-2 transition text-red-400"
                          >
                            <Trash2 className="w-4 h-4 inline" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <OrdersManager
                orders={orders}
                onUpdateStatus={handleUpdateOrderStatus}
                onOpenDetail={handleOpenOrderDetails}
                onDeleteOrder={handleDeleteOrder}
                addToast={addToast}
              />
            )}

            {/* Shipping Tab */}
            {activeTab === 'shipping' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-semibold">Futárszolgálatok kezelése</h2>
                  <button
                    onClick={() => {
                      setEditingShipping(null);
                      setShowShippingModal(true);
                    }}
                    className="btn-magic rounded-lg px-4 py-2 flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Új futárszolgálat
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {shippingOptions.map((option) => (
                    <div key={option.id} className="glass rounded-xl p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold mb-1">{option.name}</h3>
                          <p className="text-sm text-gray-400">{option.estimated_days}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          option.active 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {option.active ? 'Aktív' : 'Inaktív'}
                        </span>
                      </div>

                      <div className="text-3xl font-bold text-yellow-200 mb-4">
                        {formatPrice(option.price)}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingShipping(option);
                            setShowShippingModal(true);
                          }}
                          className="flex-1 bg-white/10 hover:bg-white/20 rounded-lg py-2 transition"
                        >
                          <Edit className="w-4 h-4 inline" /> Szerkesztés
                        </button>
                        <button
                          onClick={() => handleDeleteShipping(option.id)}
                          className="flex-1 bg-red-500/20 hover:bg-red-500/30 rounded-lg py-2 transition text-red-400"
                        >
                          <Trash2 className="w-4 h-4 inline" /> Törlés
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'services' && (
              <div className="space-y-6">
                {/* Service Categories Section */}
                <div className="glass rounded-xl p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Szolgáltatás kategóriák</h2>
                    <button
                      onClick={() => {
                        setEditingCategory(null);
                        setShowCategoryModal(true);
                      }}
                      className="btn-magic rounded-lg px-4 py-2 flex items-center gap-2 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Új kategória
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {serviceCategories.map((cat) => (
                      <div key={cat.id} className="bg-white/5 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{cat.name}</span>
                          <span className="text-xs text-gray-500">#{cat.order}</span>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => {
                              setEditingCategory(cat);
                              setShowCategoryModal(true);
                            }}
                            className="flex-1 bg-white/10 hover:bg-white/20 rounded py-1 text-xs transition"
                          >
                            <Edit className="w-3 h-3 inline" />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(cat.id)}
                            className="flex-1 bg-red-500/20 hover:bg-red-500/30 rounded py-1 text-xs transition text-red-400"
                          >
                            <Trash2 className="w-3 h-3 inline" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Services Section */}
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-semibold">Szolgáltatások</h2>
                  <button
                    onClick={() => {
                      setEditingService(null);
                      setShowServiceModal(true);
                    }}
                    className="btn-magic rounded-lg px-4 py-2 flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Új szolgáltatás
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {services.map((service) => {
                    const category = serviceCategories.find(c => c.id === service.category_id);
                    return (
                      <div key={service.id} className="glass rounded-xl overflow-hidden">
                        {/* Service Image */}
                        <div className="h-40 bg-gradient-to-br from-purple-900/20 to-black relative">
                          {service.images && service.images.length > 0 ? (
                            <img
                              src={formatImageUrl(service.images[0])}
                              alt={service.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Sparkles className="w-12 h-12 text-yellow-200/30" />
                            </div>
                          )}
                          
                          {/* Status Badge */}
                          <span className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold ${
                            service.active 
                              ? 'bg-green-500/20 text-green-400 backdrop-blur-sm' 
                              : 'bg-red-500/20 text-red-400 backdrop-blur-sm'
                          }`}>
                            {service.active ? 'Aktív' : 'Inaktív'}
                          </span>

                          {/* Category Badge */}
                          {category && (
                            <span className="absolute top-2 left-2 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs text-yellow-200">
                              {category.name}
                            </span>
                          )}
                        </div>

                        {/* Service Info */}
                        <div className="p-4">
                          <h3 className="text-lg font-semibold mb-2 line-clamp-1">{service.name}</h3>
                          <p className="text-sm text-gray-400 mb-3 line-clamp-2">{service.description}</p>
                          
                          <div className="text-2xl font-bold text-yellow-200 mb-4">
                            {formatPrice(service.price)}
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingService(service);
                                setShowServiceModal(true);
                              }}
                              className="flex-1 bg-white/10 hover:bg-white/20 rounded-lg py-2 transition text-sm"
                            >
                              <Edit className="w-4 h-4 inline" /> Szerkesztés
                            </button>
                            <button
                              onClick={() => handleDeleteService(service.id)}
                              className="flex-1 bg-red-500/20 hover:bg-red-500/30 rounded-lg py-2 transition text-red-400 text-sm"
                            >
                              <Trash2 className="w-4 h-4 inline" /> Törlés
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {services.length === 0 && (
                  <div className="text-center py-12 glass rounded-xl">
                    <Sparkles className="w-16 h-16 text-yellow-200/30 mx-auto mb-4" />
                    <p className="text-gray-400">Még nincs szolgáltatás. Hozd létre az elsőt!</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'blog' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-semibold">Blog & Podcast bejegyzések</h2>
                  <button
                    onClick={() => {
                      setEditingPost(null);
                      setShowPostModal(true);
                    }}
                    className="btn-magic rounded-lg px-4 py-2 flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Új bejegyzés
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {posts.map((post) => (
                    <div key={post.id} className="glass rounded-xl overflow-hidden">
                      <div className="flex gap-4 p-6">
                        {/* Cover Image */}
                        <div className="w-32 h-32 flex-shrink-0">
                          {post.cover_image ? (
                            <img
                              src={formatImageUrl(post.cover_image)}
                              alt={post.title}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-blue-900/20 to-black rounded-lg flex items-center justify-center">
                              <FileText className="w-12 h-12 text-blue-200/30" />
                            </div>
                          )}
                        </div>

                        {/* Post Info */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="text-xl font-semibold mb-1">{post.title}</h3>
                              <div className="flex items-center gap-3 text-sm text-gray-400">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  post.type === 'blog' 
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : 'bg-purple-500/20 text-purple-400'
                                }`}>
                                  {post.type === 'blog' ? 'Blog' : 'Podcast'}
                                </span>
                                <span>{new Date(post.created_at).toLocaleDateString('hu-HU')}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  post.active 
                                    ? 'bg-green-500/20 text-green-400' 
                                    : 'bg-red-500/20 text-red-400'
                                }`}>
                                  {post.active ? 'Aktív' : 'Inaktív'}
                                </span>
                              </div>
                            </div>
                          </div>

                          <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                            {post.excerpt || post.content.substring(0, 150) + '...'}
                          </p>

                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingPost(post);
                                setShowPostModal(true);
                              }}
                              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition text-sm"
                            >
                              <Edit className="w-4 h-4 inline mr-1" /> Szerkesztés
                            </button>
                            <button
                              onClick={() => handleDeletePost(post.id)}
                              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition text-red-400 text-sm"
                            >
                              <Trash2 className="w-4 h-4 inline mr-1" /> Törlés
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {posts.length === 0 && (
                  <div className="text-center py-12 glass rounded-xl">
                    <FileText className="w-16 h-16 text-blue-200/30 mx-auto mb-4" />
                    <p className="text-gray-400">Még nincs bejegyzés. Hozd létre az elsőt!</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'about' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-semibold">Rólam szekció szerkesztése</h2>
                </div>

                <div className="glass rounded-xl p-6">
                  <p className="text-gray-400 mb-4">
                    Ez a szöveg jelenik meg a főoldalon a "Rólam" szekcióban (#rolam).
                  </p>
                  
                  <textarea
                    value={aboutContent}
                    onChange={(e) => setAboutContent(e.target.value)}
                    rows={12}
                    className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white resize-none"
                    placeholder="Írd meg a bemutatkozó szöveget..."
                  />

                  <p className="text-xs text-gray-500 mt-2 mb-4">
                    💡 <strong>Tipp:</strong> Használj Enter-t új sorokhoz. Egyszerű szöveg, formázás nélkül.
                  </p>

                  <button
                    onClick={handleSaveAbout}
                    className="btn-magic rounded-lg px-6 py-3 flex items-center gap-2"
                  >
                    💾 Mentés
                  </button>
                </div>

                <div className="glass rounded-xl p-6 bg-yellow-200/5 border-yellow-200/20">
                  <h3 className="text-lg font-semibold text-yellow-200 mb-2">📝 Előnézet</h3>
                  <div className="text-gray-300 whitespace-pre-line">
                    {aboutContent || 'Még nincs tartalom...'}
                  </div>
                </div>
              </div>
            )}

            {/* Payment Methods Tab */}
            {activeTab === 'payment-methods' && (
              <div className="glass rounded-2xl p-6" data-testid="payment-methods-section">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold">Fizetési módok kezelése</h2>
                  <button
                    onClick={() => {
                      setEditingPaymentMethod(null);
                      setShowPaymentModal(true);
                    }}
                    className="btn-magic rounded-lg px-4 py-2 flex items-center gap-2"
                    data-testid="new-payment-method-btn"
                  >
                    <Plus className="w-5 h-5" />
                    Új fizetési mód
                  </button>
                </div>

                {paymentMethods.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <CreditCard className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Még nincs fizetési mód. Kattints az "Új fizetési mód" gombra!</p>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="payment-methods-list">
                    {paymentMethods.map((method) => (
                      <div
                        key={method.id}
                        className="glass rounded-xl p-6 hover:border-yellow-200/30 transition"
                        data-testid={`payment-method-${method.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-xl font-semibold text-white">{method.name}</h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                method.active 
                                  ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                                  : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                              }`}>
                                {method.active ? 'Aktív' : 'Inaktív'}
                              </span>
                              <span className="px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30">
                                {method.type}
                              </span>
                            </div>
                            
                            {method.description && (
                              <p className="text-gray-400 text-sm mb-2">{method.description}</p>
                            )}
                            
                            {method.instructions && (
                              <div className="mt-3 p-3 bg-white/5 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Utasítások:</p>
                                <p className="text-sm text-gray-300 whitespace-pre-line">{method.instructions}</p>
                              </div>
                            )}
                            
                            <p className="text-xs text-gray-500 mt-2">Sorrend: {method.order}</p>
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            {/* Toggle Active/Inactive */}
                            <button
                              onClick={() => handleTogglePaymentMethod(method.id, method.active)}
                              className={`px-4 py-2 rounded-lg transition ${
                                method.active
                                  ? 'bg-gray-500 hover:bg-gray-600 text-white'
                                  : 'bg-green-500 hover:bg-green-600 text-white'
                              }`}
                              title={method.active ? 'Deaktiválás' : 'Aktiválás'}
                              data-testid={`toggle-payment-${method.id}`}
                            >
                              {method.active ? 'Kikapcsolás' : 'Bekapcsolás'}
                            </button>
                            
                            {/* Edit */}
                            <button
                              onClick={() => {
                                setEditingPaymentMethod(method);
                                setShowPaymentModal(true);
                              }}
                              className="p-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition"
                              title="Szerkesztés"
                              data-testid={`edit-payment-${method.id}`}
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            
                            {/* Delete */}
                            <button
                              onClick={() => handleDeletePaymentMethod(method.id)}
                              className="p-2 bg-red-500 hover:bg-red-600 rounded-lg transition"
                              title="Törlés"
                              data-testid={`delete-payment-${method.id}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}


            {/* Gallery Tab */}
            {activeTab === 'gallery' && (
              <div className="glass rounded-2xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold">Galéria kezelése</h2>
                  <button
                    onClick={() => {
                      setEditingGalleryItem(null);
                      setShowGalleryModal(true);
                    }}
                    className="btn-magic rounded-lg px-4 py-2 flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Új kép hozzáadása
                  </button>
                </div>

                {galleryItems.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Camera className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Még nincs galéria elem. Kattints az "Új kép hozzáadása" gombra!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {galleryItems.map((item) => (
                      <div
                        key={item.id}
                        className="glass rounded-xl overflow-hidden hover:border-yellow-200/30 transition group"
                      >
                        <div className="relative h-48 bg-black/30">
                          <img
                            src={formatImageUrl(item.image_url)}
                            alt={item.caption}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                            <button
                              onClick={() => {
                                setEditingGalleryItem(item);
                                setShowGalleryModal(true);
                              }}
                              className="p-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteGalleryItem(item.id)}
                              className="p-2 bg-red-500 hover:bg-red-600 rounded-lg transition"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        <div className="p-4">
                          <p className="text-sm text-gray-300 mb-2 line-clamp-2">{item.caption}</p>
                          <p className="text-xs text-gray-500">Sorrend: {item.order}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </>
        )}
      </div>

      {/* Product Modal */}
      <ProductModal
        isOpen={showProductModal}
        onClose={() => {
          setShowProductModal(false);
          setEditingProduct(null);
        }}
        product={editingProduct}
        onSuccess={fetchData}
      />

      <PaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setEditingPaymentMethod(null);
        }}
        paymentMethod={editingPaymentMethod}
        onSave={handleSavePaymentMethod}
      />

      {/* Shipping Modal */}
      <ShippingModal
        isOpen={showShippingModal}
        onClose={() => {
          setShowShippingModal(false);
          setEditingShipping(null);
        }}
        shipping={editingShipping}
        onSave={handleSaveShipping}
      />

      <ServiceCategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
        onSave={handleSaveCategory}
      />

      <ServiceModal
        isOpen={showServiceModal}
        onClose={() => {
          setShowServiceModal(false);
          setEditingService(null);
        }}
        service={editingService}
        categories={serviceCategories}
        onSave={handleSaveService}
      />

      <PostModal
        isOpen={showPostModal}
        onClose={() => {
          setShowPostModal(false);
          setEditingPost(null);
        }}
        post={editingPost}
        onSave={handleSavePost}
      />

      <GalleryModal
        isOpen={showGalleryModal}
        onClose={() => {
          setShowGalleryModal(false);
          setEditingGalleryItem(null);
        }}
        galleryItem={editingGalleryItem}
        onSave={handleSaveGalleryItem}
      />

      <OrderDetailModal
        isOpen={showOrderDetail}
        onClose={() => {
          setShowOrderDetail(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
      />

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .section-title {
          font-family: "Great Vibes", cursive;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
        .section-title span {
          color: #ffffff;
        }
        .btn-magic {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          border: 1px solid hsl(0 0% 100% / 0.14);
        }
        .btn-magic:before {
          content: '';
          position: absolute;
          inset: -200%;
          background: conic-gradient(from 0deg, transparent, hsl(45 86% 62% / 0.25), transparent 30%);
          animation: spin 6s linear infinite;
          z-index: -1;
          filter: blur(12px);
        }
        @keyframes spin {
          to { transform: rotate(1turn); }
        }
      `}</style>
    </div>
  );
};

export default Admin;